import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import UserRegistration from './steps/UserRegistration';
import VendorProfile from './steps/VendorProfile';
import BoothSelection from './steps/BoothSelection';
import PaymentStep from './steps/PaymentStep';
import PendingApproval from './steps/PendingApproval';
import MainLayout from '../layout/MainLayout';
import { useAuth } from '../../context/AuthContext';
import StyledButton from '../common/StyledButton';
import HandmadeAgreementStep from './steps/HandmadeAgreementStep';
import ProductPhotosStep from './steps/ProductPhotosStep';
import api from '../../services/api';
import {
  LOGIN_REASON_MESSAGES,
  persistLoginBanner,
} from '../../utils/loginMessages';

const steps = [
  'User Information',
  'Vendor Profile',
  'Product Photos',
  'Handmade Agreement',
  'Booth Selection',
  'Pending Approval'
];

const VendorOnboarding = () => {
  const [activeStep, setActiveStep] = useState(null); // null until determined
  const [error, setError] = useState('');
  const [registrationData, setRegistrationData] = useState({});
  const [activeFestival, setActiveFestival] = useState(null);
  // Distinguish "still loading" / "fetch failed" from "registration closed" so
  // a slow or failed /festivals/active call does not trap users behind the
  // "not yet available" overlay.
  const [festivalStatusLoading, setFestivalStatusLoading] = useState(true);
  const [festivalStatusError, setFestivalStatusError] = useState(false);
  const [festivalStatusRetry, setFestivalStatusRetry] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  // Pull the active festival so the "registration not yet open" overlay reflects
  // the admin-controlled registration window instead of a hardcoded message.
  useEffect(() => {
    let cancelled = false;
    setFestivalStatusLoading(true);
    setFestivalStatusError(false);
    api
      .get('/festivals/active')
      .then(({ data }) => {
        if (cancelled) return;
        setActiveFestival(data);
        setFestivalStatusError(false);
      })
      .catch(() => {
        if (cancelled) return;
        setActiveFestival(null);
        setFestivalStatusError(true);
      })
      .finally(() => {
        if (!cancelled) setFestivalStatusLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [festivalStatusRetry]);

  // Only treat registration as closed once we know the festival status.
  // While loading (or on fetch error), do not show the closed overlay.
  const registrationClosed =
    !festivalStatusLoading &&
    !festivalStatusError &&
    !!activeFestival &&
    activeFestival.status !== 'open';
  const festivalLabel = activeFestival
    ? activeFestival.name || `${activeFestival.year} Gingham Tree`
    : 'Gingham Tree';

  // Dynamically determine the correct step
  useEffect(() => {
    const determineStep = async () => {
      try {
        // Check for step param in URL
        const stepParam = searchParams.get('step');
        if (stepParam) {
          switch (stepParam) {
            case 'user_info':
              setActiveStep(0);
              return;
            case 'vendor_profile':
              setActiveStep(1);
              return;
            case 'product_photos':
              setActiveStep(2);
              return;
            case 'handmade_agreement':
              setActiveStep(3);
              return;
            case 'booth_selection':
              setActiveStep(4);
              return;
            case 'pending_approval':
              setActiveStep(5);
              return;
            default:
              setActiveStep(0);
              return;
          }
        }
        // If no step param, check registration status if authenticated
        if (isAuthenticated) {
          try {
            const { data } = await api.get('/vendor/registration-status');
            console.log('Registration status response:', data);

            if (data.length > 0) {
              const vendor = data[0]; // Get the first vendor
              console.log('Using vendor data:', vendor);

              switch (vendor.registration_step) {
                case 'user_info':
                  setActiveStep(0);
                  return;
                case 'vendor_profile':
                  setActiveStep(1);
                  return;
                case 'product_photos':
                  setActiveStep(2);
                  return;
                case 'handmade_agreement':
                  setActiveStep(3);
                  return;
                case 'booth_selection':
                  setActiveStep(4);
                  return;
                case 'pending_approval':
                  setActiveStep(5);
                  return;
                default:
                  console.log('Unknown registration step:', vendor.registration_step);
                  // A null/unknown registration_step covers two real cases:
                  //   1) brand-new user who just signed up but hasn't filled
                  //      out Vendor Profile yet,
                  //   2) returning vendor whose enrollment row for the active
                  //      festival hasn't been created yet (no step assigned).
                  // Both should land on Vendor Profile so the user can either
                  // create or update their business profile for this year.
                  setActiveStep(1);
                  return;
              }
            } else {
              console.log('No vendor data found, defaulting to step 1');
              // Authenticated user with no vendors row at all (signed up but
              // bailed before profile). POST /vendor/profile will create the
              // vendors row + enrollment, so Vendor Profile is the right
              // place to resume.
              setActiveStep(1);
            }
          } catch (err) {
            console.error('Error fetching registration status:', err);
            setActiveStep(1); // Default to vendor profile if cannot fetch
          }
        } else {
          setActiveStep(0); // Not authenticated, start at user info
        }
      } catch (err) {
        setError(
          "We couldn't figure out where you left off. Refresh the page; if it keeps happening, log out and back in."
        );
        setActiveStep(0);
      }
    };
    determineStep();
    // Only run on mount or when searchParams/isAuthenticated changes
  }, [searchParams, isAuthenticated]);

  const handleNext = async (data) => {
    try {
      setError('');
      
      if (activeStep === 0) {
        // Handle user registration
        try {
          const response = await api.post('/auth/signup', data);
          
          // Automatically log the user in after successful signup
          await api.post('/auth/login', { 
            email: data.email, 
            password: data.password 
          });

          // Reload the page to update authentication context
          window.location.reload();
          return;
        } catch (err) {
          console.log('Signup error:', err.response?.data);
          
          // Check if the error is due to existing email
          if (err.response?.data?.error === 'Email already in use') {
            console.log('Redirecting to login due to existing email...');
            persistLoginBanner(LOGIN_REASON_MESSAGES.existing_account);
            navigate('/login', {
              replace: true,
              state: {
                message: LOGIN_REASON_MESSAGES.existing_account,
                email: data.email,
              },
            });
            return;
          }
          
          // For other errors, throw them to be handled by the error state
          throw new Error(
            err.response?.data?.error ||
              err.response?.data?.message ||
              "We couldn't create your account. Double-check your email and password, then try again. If this keeps happening, contact the festival organizers."
          );
        }
      }

      if (activeStep === 1) {
        // Handle vendor profile
        try {
          const { data: profileData } = await api.post('/vendor/profile', data);
          setRegistrationData(prev => ({ ...prev, ...profileData }));

          // Update registration step only after profile is created
          await api.put('/vendor/registration-step', {
            step: 'product_photos'
          });

          setActiveStep(2);
          return;
        } catch (err) {
          throw new Error(
            err.response?.data?.error ||
              "We couldn't save your vendor profile. Make sure every required field is filled in, then click Next again."
          );
        }
      }

      if (activeStep === 2) {
        // Product photos step. Uploads/deletes happen inline in the step
        // component; here we just persist the step advance so the resume
        // links land on handmade_agreement next time.
        setRegistrationData(prev => ({ ...prev, ...data }));
        try {
          await api.put('/vendor/registration-step', {
            step: 'handmade_agreement'
          });
          setActiveStep(3);
          return;
        } catch (err) {
          throw new Error(
            err.response?.data?.error ||
              "We saved your photos but couldn't move you to the next step. Refresh the page and you should see the Handmade Agreement."
          );
        }
      }

      if (activeStep === 3) {
        // Handle handmade agreement
        const updatedData = {
          ...registrationData,
          ...data,
        };
        setRegistrationData(updatedData);

        try {
          // Save agreement to vendor profile
          await api.patch('/vendor/profile/agreement', { 
            handmade_agreement_accepted: true 
          });

          // Update registration step to booth_selection
          await api.put('/vendor/registration-step', { 
            step: 'booth_selection'
          });

          setActiveStep(4);
          return;
        } catch (err) {
          throw new Error(
            err.response?.data?.error ||
              "We couldn't record your agreement. Re-check both the checkbox and signature, then click Next."
          );
        }
      }

      if (activeStep === 4) {
        // Handle booth selection - actually register the booth in the database
        try {
          // Get the current vendor ID from registration status
          const { data: statusData } = await api.get('/vendor/registration-status');
          const vendorId = statusData[0]?.vendor_id;
          
          if (!vendorId) {
            throw new Error('Could not find vendor ID. Please try again or contact support.');
          }

          console.log('Registering booths:', {
            vendorId,
            booths: data
          });

          // Register each selected booth and store registration IDs
          const registeredBooths = [];
          for (const booth of data) {
            const response = await api.post('/vendor/booth-registration', {
              booth_id: booth.id,
              needs_electricity: booth.needs_electricity,
              special_requests: booth.special_requests || null,
              skip_emails: true, // Don't send emails during initial selection
              vendor_id: vendorId
            });
            
            // Store both the booth data and the registration ID
            registeredBooths.push({
              ...booth,
              registration_id: response.data.id
            });
          }

          // Store the registered booth data with registration IDs
          setRegistrationData(prev => ({
            ...prev,
            boothData: registeredBooths,
          }));

          // Update registration step to pending_approval
          await api.put('/vendor/registration-step', { 
            step: 'pending_approval'
          });

          setActiveStep(5);
          return;
        } catch (err) {
          console.error('Booth registration error:', err);
          // BOOTH_TAKEN is the explicit code the booth-registration endpoint
          // sets when a soft hold has expired or another vendor reserved the
          // booth before submit. Anything else gets a generic-but-actionable
          // message asking the user to refresh the booth map.
          const code = err.response?.data?.code;
          const apiError = err.response?.data?.error;
          if (code === 'BOOTH_TAKEN') {
            throw new Error(
              apiError ||
                'Someone else just claimed one of your selected booths. Refresh the booth map and pick another booth.'
            );
          }
          throw new Error(
            apiError ||
              "Your booths couldn't be reserved. Refresh the booth map and try again. If the problem keeps happening, contact the festival organizers."
          );
        }
      }

      if (activeStep === 5) {
        // Handle pending approval
        setRegistrationData(prev => ({ ...prev, ...data }));
        navigate('/vendor/dashboard');
        return;
      }

      setActiveStep(prev => prev + 1);
    } catch (err) {
      console.error('Error in handleNext:', err);
      setError(err.message);
    }
  };

  const handleBack = () => {
    // If user is authenticated and tries to go back from vendor profile,
    // redirect to dashboard instead of user registration
    if (isAuthenticated && activeStep === 1) {
      navigate('/vendor/dashboard');
      return;
    }
    setActiveStep(prev => prev - 1);
  };

  const handleComplete = async (data) => {
    try {
      setError('');
      setRegistrationData(prev => ({ ...prev, ...data }));
      navigate('/vendor/dashboard');
    } catch (err) {
      console.error('Error in handleComplete:', err);
      setError(err.message);
    }
  };

  // If user is not authenticated and tries to access vendor profile or booth selection,
  // redirect to login
  useEffect(() => {
    if (!isAuthenticated && activeStep > 0) {
      persistLoginBanner(LOGIN_REASON_MESSAGES.vendor_registration);
      navigate('/login', {
        replace: true,
        state: {
          message: LOGIN_REASON_MESSAGES.vendor_registration,
        },
      });
    }
  }, [isAuthenticated, activeStep, navigate]);

  return (
    <MainLayout>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, position: 'relative' }}>
        {/* Loading / error / closed-registration overlays. Admins can preview */}
        {/* the flow without the closed overlay. */}
        {(festivalStatusLoading || festivalStatusError || (registrationClosed && !isAdmin)) && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              p: 4,
            }}
          >
            {festivalStatusLoading ? (
              <>
                <CircularProgress sx={{ mb: 2, color: '#1a237e' }} />
                <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
                  Checking registration availability…
                </Typography>
              </>
            ) : festivalStatusError ? (
              <>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: '#1a237e',
                    mb: 2,
                    textAlign: 'center',
                  }}
                >
                  We couldn&apos;t check registration status
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#666',
                    textAlign: 'center',
                    maxWidth: 500,
                    mb: 3,
                  }}
                >
                  Please check your connection and try again.
                </Typography>
                <StyledButton
                  onClick={() => setFestivalStatusRetry((n) => n + 1)}
                >
                  Try again
                </StyledButton>
              </>
            ) : (
              <>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#1a237e',
                    mb: 2,
                    textAlign: 'center',
                  }}
                >
                  Registration for {festivalLabel} is not yet available
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#666',
                    textAlign: 'center',
                    maxWidth: 500,
                  }}
                >
                  Check back soon for updates on {festivalLabel} registration.
                </Typography>
              </>
            )}
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper elevation={0} sx={{ p: 4, backgroundColor: '#fafbfc' }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Vendor Registration
          </Typography>

          <Stepper activeStep={activeStep ?? 0} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box>
            {activeStep === 0 && !isAuthenticated && (
              <UserRegistration
                onNext={handleNext}
                initialData={registrationData}
              />
            )}
            {activeStep === 1 && (
              <VendorProfile
                onNext={handleNext}
                onBack={handleBack}
                initialData={registrationData}
                eventDate={activeFestival?.event_date}
              />
            )}
            {activeStep === 2 && (
              <ProductPhotosStep
                onNext={handleNext}
                onBack={handleBack}
                initialData={registrationData}
              />
            )}
            {activeStep === 3 && (
              <HandmadeAgreementStep
                onNext={handleNext}
                onBack={handleBack}
                initialData={registrationData}
              />
            )}
            {activeStep === 4 && (
              <BoothSelection
                onNext={handleNext}
                onBack={handleBack}
                initialData={registrationData}
              />
            )}
            {activeStep === 5 && (
              <PendingApproval
                onNext={handleComplete}
                onBack={handleBack}
                vendorData={registrationData}
              />
            )}
            {(activeStep === null || activeStep < 0 || activeStep > 5) && (
              <Alert severity="error">Invalid registration step. Please start over.</Alert>
            )}
          </Box>
        </Paper>
      </Container>
    </MainLayout>
  );
};

export default VendorOnboarding; 